/**
 * Per-key in-flight request coalescing.
 *
 * Collapses calls that overlap IN TIME for the same key into a single
 * promise — every concurrent caller piggybacks on the one in-flight request
 * and fires zero extra network calls. The moment that request settles the
 * key is released, so a genuinely-later call goes through normally. Unlike a
 * time-based cooldown this can NEVER suppress a needed refetch → no staleness.
 *
 * Used to kill the dashboard mount-storm: ~12 widgets each call their store's
 * fetch action within the same instant. With coalescing they collapse to one
 * call. The dashboard hydrator additionally `preregister`s each slice against
 * the single `dashboard-summary` request so those widget fetches piggyback on
 * it (see services/dashboardHydrator.js).
 *
 * This is the same single-flight pattern already proven in
 * services/api/sessionApi.js (`_sessionPromise`), generalised to N keys.
 */

const _inflight = new Map(); // key -> Promise

function _track(key, promise) {
  // Wrap so the key is released once the promise settles (success or failure).
  const tracked = Promise.resolve(promise).finally(() => {
    if (_inflight.get(key) === tracked) _inflight.delete(key);
  });
  _inflight.set(key, tracked);
  return tracked;
}

/**
 * Run `fn` under `key`, coalescing concurrent callers. If a promise is
 * already registered for `key` (by another caller OR by `preregister`),
 * the existing promise is returned and `fn` is NOT invoked.
 */
export function coalesce(key, fn) {
  const existing = _inflight.get(key);
  if (existing) return existing;
  return _track(key, Promise.resolve().then(fn));
}

/**
 * Register an externally-produced promise for `key`. Subsequent `coalesce`
 * callers for the same key piggyback on it. The dashboard hydrator uses this
 * to make widget fetch actions ride the single summary request.
 */
export function preregister(key, promise) {
  return _track(key, promise);
}

/** True if a request is currently registered for `key`. */
export function isInflight(key) {
  return _inflight.has(key);
}

/** Test helper — clear all in-flight state. */
export function _resetCoalesce() {
  _inflight.clear();
}
